import { Injectable, inject } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';

import { SnackbarComponent, SnackbarData, SnackbarType } from './snackbar';
import { HttpErrorResponse } from '@angular/common/http';

export interface SnackbarOptions {
  /**
   * Overrides the global 5s default. One value, applied to both the dismissal
   * and the countdown bar — which is the reason to go through this service
   * rather than `MatSnackBar` directly.
   */
  duration?: number;
  /**
   * Interpolation values for the message key. The text stays one translation
   * with a placeholder wherever each language needs it:
   *
   * ```ts
   * // "deleted": "Spray rig '{{name}}' deleted successfully."
   * this.snackbar.success('components.sprayRigs.messages.deleted', { params: { name } });
   * ```
   */
  params?: Record<string, unknown>;
}

/**
 * The way features raise a notification popup:
 *
 * ```ts
 * private readonly snackbar = inject(SnackbarService);
 *
 * this.snackbar.success('views.projects.messages.saved');
 * ```
 *
 * `message` is a translation key — `SnackbarComponent` translates it.
 */
@Injectable({ providedIn: 'root' })
export class SnackbarService {
  private readonly matSnackBar = inject(MatSnackBar);

  success(message: string, options?: SnackbarOptions): MatSnackBarRef<SnackbarComponent> {
    return this.open(message, 'success', options);
  }

  error(message: string, options?: SnackbarOptions): MatSnackBarRef<SnackbarComponent> {
    return this.open(message, 'error', options);
  }

  info(message: string, options?: SnackbarOptions): MatSnackBarRef<SnackbarComponent> {
    return this.open(message, 'info', options);
  }

  warning(message: string, options?: SnackbarOptions): MatSnackBarRef<SnackbarComponent> {
    return this.open(message, 'warning', options);
  }

  apiError(error: HttpErrorResponse, message?: string, options?: SnackbarOptions): MatSnackBarRef<SnackbarComponent> | void {
    const errorMessage = message ?? error.message ?? 'An unknown error occurred';
    if (error.status >= 400 && error.status <= 499 && error.status !== 401) {
      return this.open(errorMessage, 'error', options);
    }
  }

  /**
   * The returned ref is the hook for undo-style follow-ups: `onAction()` fires
   * only when the user clicks Dismiss, never when the countdown runs out.
   */
  private open(
    message: string,
    type: SnackbarType,
    options?: SnackbarOptions,
  ): MatSnackBarRef<SnackbarComponent> {
    const { duration, params } = options ?? {};

    return this.matSnackBar.openFromComponent<SnackbarComponent, SnackbarData>(SnackbarComponent, {
      // Spread conditionally: Material merges this over its defaults, so an
      // explicit `duration: undefined` would replace the global 5s with "never
      // dismiss" rather than fall through to it.
      ...(duration === undefined ? {} : { duration }),
      data: { message, type, duration, params },
    });
  }
}
